'use client';

import { useEffect, useRef, useState } from 'react';
import { ClipStatusIcon } from './clip-status-icon';

interface IClipSlideProps {
  src: string;
  transcript?: string | null;
  language: string;
  parentTitle?: string | null;
  collectionTitle?: string | null;
  isActive: boolean;
  isMuted: boolean;
  isPaused: boolean;
  loop?: boolean;
  onTap: () => void;
  onEnded?: () => void;
}

function resolveStatus(
  isMuted: boolean,
  isPaused: boolean,
  isHovered: boolean,
): 'muted' | 'play' | 'pause' | null {
  if (isMuted) {
    return 'muted';
  }
  if (isPaused) {
    return 'play';
  }
  return isHovered ? 'pause' : null;
}

export function ClipSlide({
  src,
  transcript,
  language,
  parentTitle,
  collectionTitle,
  isActive,
  isMuted,
  isPaused,
  loop = false,
  onTap,
  onEnded,
}: IClipSlideProps): React.JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (isActive) {
      video.currentTime = 0;
      video.play().catch(() => undefined);
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isActive]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isActive) {
      return;
    }
    if (isPaused) {
      video.pause();
    } else {
      video.play().catch(() => undefined);
    }
  }, [isPaused, isActive]);

  const status = resolveStatus(isMuted, isPaused, isHovered);

  return (
    <button
      type="button"
      onClick={onTap}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      className="relative block size-full cursor-pointer bg-transparent p-0 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      aria-label={isMuted ? 'Tap to unmute video' : 'Tap to play or pause video'}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay={isActive}
        muted
        loop={loop}
        playsInline
        preload={isActive ? 'auto' : 'none'}
        onEnded={onEnded}
        className="size-full object-cover"
      />
      <div className="pointer-events-none absolute inset-x-5 bottom-4 flex flex-col items-center gap-2">
        {parentTitle && collectionTitle && (
          <div className="max-w-full text-balance rounded-lg bg-white/10 px-3 py-1 text-center text-[11px] font-medium text-white shadow-md ring-1 ring-white/15 backdrop-blur-md">
            <span className="font-semibold">{parentTitle}</span>
            {' › '}
            <span className="opacity-80">{collectionTitle}</span>
          </div>
        )}
        {transcript && (
          <div
            className="max-w-full text-balance rounded-xl bg-white/10 px-3 py-2 text-center text-base font-semibold leading-snug text-white shadow-lg ring-1 ring-white/15 backdrop-blur-md"
            lang={language}
          >
            {transcript}
          </div>
        )}
      </div>
      <div
        className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
          status ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {status && <ClipStatusIcon status={status} />}
      </div>
    </button>
  );
}
